/**
 * 同期再生のマスタークロック(計画書§6)。
 * AudioBufferSourceNode の loop=true でギャップレスループし、
 * AudioContext.currentTime 起点でループ位相 t を算出する。
 * アニメーション側は毎フレーム setTime(t) でこのクロックに強制追従する。
 *
 * 音声ファイルが無い/デコード失敗の場合も、無音のままクロックとしては動作する。
 */
export class SyncedAudio {
  private ctx: AudioContext;
  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  private gainNode: GainNode;
  private startedAt = 0;
  private running = false;
  readonly loopSeconds: number;
  /** 実際に採用された音声URL(HUD用)。無音クロック時はnull */
  loadedUrl: string | null = null;

  constructor(loopSeconds: number) {
    this.loopSeconds = loopSeconds;
    this.ctx = new AudioContext();
    this.gainNode = this.ctx.createGain();
    this.gainNode.connect(this.ctx.destination);
  }

  /**
   * 候補URLを先頭から順に試し、最初にデコードできたものを採用する。
   * (Opus/.webm → AAC/.m4a のフォールバックはこの順序で実現)
   */
  async load(baseUrl: string, candidates: string[]): Promise<void> {
    for (const rel of candidates) {
      const url = baseUrl + rel;
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const data = await res.arrayBuffer();
        this.buffer = await this.ctx.decodeAudioData(data);
        this.loadedUrl = url;
        return;
      } catch (e) {
        console.warn(`[SyncedAudio] デコード失敗: ${url}`, e);
      }
    }
    if (candidates.length > 0) {
      console.warn('[SyncedAudio] 再生可能な音声がありません。無音クロックで続行します。');
    }
  }

  /** 必ずユーザージェスチャー(タップ)ハンドラ内から呼ぶこと(iOS自動再生制限) */
  async start(): Promise<void> {
    if (this.running) return;
    await this.ctx.resume();
    if (this.buffer) {
      const src = this.ctx.createBufferSource();
      src.buffer = this.buffer;
      src.loop = true;
      // 圧縮音声(AAC/Opus)はエンコーダ遅延・パディングでバッファ長が
      // わずかに伸びることがある。余剰をループ区間から除外してクロック
      // (loopSeconds)と厳密に一致させ、周回ごとの位相ずれ蓄積を防ぐ
      const excess = this.buffer.duration - this.loopSeconds;
      if (excess > 0 && excess < 0.25) {
        src.loopEnd = this.loopSeconds;
      } else if (Math.abs(excess) > 0.05) {
        console.warn(
          `[SyncedAudio] 音声長(${this.buffer.duration.toFixed(2)}s)がloopSeconds(${this.loopSeconds}s)と不一致。位相ずれの原因になります。`,
        );
      }
      src.connect(this.gainNode);
      src.start();
      this.source = src;
    }
    this.startedAt = this.ctx.currentTime;
    this.running = true;
  }

  /** ループ位相 t ∈ [0, loopSeconds)。マスタークロック */
  getTime(): number {
    if (!this.running) return 0;
    const elapsed = this.ctx.currentTime - this.startedAt;
    return ((elapsed % this.loopSeconds) + this.loopSeconds) % this.loopSeconds;
  }

  setVolume(v: number): void {
    this.gainNode.gain.value = v;
  }

  async suspend(): Promise<void> {
    if (this.running) await this.ctx.suspend();
  }

  async resume(): Promise<void> {
    if (this.running) await this.ctx.resume();
  }

  dispose(): void {
    this.source?.stop();
    this.source?.disconnect();
    this.source = null;
    void this.ctx.close();
    this.running = false;
  }
}
